import React, { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import {
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlineVideoCamera,
  HiOutlinePlus,
} from "react-icons/hi2";
import { db } from "../db/firebase";
import { YoutubeVideosDisplay } from "./YoutubeVideosDisplay";
import {
  MAX_YOUTUBE_VIDEOS,
  buildYoutubeFirestoreUpdate,
  getYouTubeEmbedUrl,
  isYouTubeUrl,
  normalizeYouTubeUrl,
  prepareYoutubeLinksForSave,
} from "../utils/youtubeLinks";

interface YoutubeVideosSectionProps {
  songId: string;
  initialLinks: string[];
  onSaved?: (links: string[]) => void;
  headerPadding?: string;
  bodyPadding?: string;
  showTransposeHint?: boolean;
}

export const YoutubeVideosSection: React.FC<YoutubeVideosSectionProps> = ({
  songId,
  initialLinks,
  onSaved,
  headerPadding = "px-5 py-4",
  bodyPadding = "p-5",
  showTransposeHint = true,
}) => {
  const { t } = useTranslation();
  const [savedLinks, setSavedLinks] = useState<string[]>(initialLinks);
  const [youtubeLinks, setYoutubeLinks] = useState<string[]>(initialLinks);
  const [youtubeEditing, setYoutubeEditing] = useState(false);
  const [youtubeSaving, setYoutubeSaving] = useState(false);
  const [youtubeError, setYoutubeError] = useState("");

  useEffect(() => {
    setSavedLinks(initialLinks);
    if (!youtubeEditing) {
      setYoutubeLinks(initialLinks);
    }
  }, [initialLinks, youtubeEditing]);

  const displayLinks = savedLinks
    .map(normalizeYouTubeUrl)
    .filter((link) => link && isYouTubeUrl(link));

  const startEditing = () => {
    setYoutubeError("");
    setYoutubeLinks(savedLinks.length > 0 ? [...savedLinks] : [""]);
    setYoutubeEditing(true);
  };

  const updateLinkAt = (index: number, value: string) => {
    setYoutubeLinks((prev) => prev.map((link, i) => (i === index ? value : link)));
    setYoutubeError("");
  };

  const removeLinkAt = (index: number) => {
    setYoutubeLinks((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [""];
    });
    setYoutubeError("");
  };

  const addLinkSlot = () => {
    if (youtubeLinks.length >= MAX_YOUTUBE_VIDEOS) return;
    setYoutubeLinks((prev) => [...prev, ""]);
  };

  const handleCancel = () => {
    setYoutubeLinks(savedLinks);
    setYoutubeError("");
    setYoutubeEditing(false);
  };

  const handleSave = async () => {
    const { links, error } = prepareYoutubeLinksForSave(youtubeLinks);

    if (error === "invalid") {
      setYoutubeError(t("invalidYouTubeUrl") || "Please enter a valid YouTube URL.");
      return;
    }
    if (error === "max") {
      setYoutubeError(
        t("maxYouTubeVideosReached", { max: MAX_YOUTUBE_VIDEOS }) ||
          `You can add up to ${MAX_YOUTUBE_VIDEOS} videos.`
      );
      return;
    }

    setYoutubeSaving(true);
    setYoutubeError("");
    try {
      const payload = buildYoutubeFirestoreUpdate(links);
      await updateDoc(doc(db, "songs", songId), payload);
      setSavedLinks(links);
      setYoutubeLinks(links);
      setYoutubeEditing(false);
      onSaved?.(links);
    } catch (err) {
      console.error("Error saving YouTube links:", err);
      setYoutubeError(t("failedToSaveSong") || "Failed to save song.");
    } finally {
      setYoutubeSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div
        className={`${headerPadding} border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
            {t("youtubeVideos") || "YouTube Videos"}
          </h2>
          {displayLinks.length > 0 && !youtubeEditing && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#5932EA]/10 text-[#5932EA]">
              {displayLinks.length}/{MAX_YOUTUBE_VIDEOS}
            </span>
          )}
        </div>
        {!youtubeEditing && (
          <button
            type="button"
            onClick={startEditing}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              displayLinks.length > 0
                ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-[#5932EA]/10 hover:text-[#5932EA] dark:hover:bg-[#5932EA]/20 dark:hover:text-[#5932EA]"
                : "bg-[#5932EA] text-white hover:bg-[#4a28c7]"
            }`}
          >
            <HiOutlinePencilSquare className="w-4 h-4" />
            {displayLinks.length > 0
              ? (t("edit") || "Edit")
              : (t("addYouTubeVideo") || "Add video")}
          </button>
        )}
      </div>

      <div className={bodyPadding}>
        {youtubeEditing ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("youtubeVideosHelp") || "Add up to 10 YouTube links for this song."}
              </p>
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                {youtubeLinks.filter((l) => l.trim()).length}/{MAX_YOUTUBE_VIDEOS}
              </span>
            </div>

            <div className="space-y-3">
              {youtubeLinks.map((link, index) => {
                const normalized = normalizeYouTubeUrl(link);
                const previewEmbed =
                  normalized && isYouTubeUrl(normalized)
                    ? getYouTubeEmbedUrl(normalized)
                    : null;

                return (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 dark:border-gray-600 p-3 space-y-3"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <label
                          htmlFor={`youtube-link-${songId}-${index}`}
                          className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5"
                        >
                          {t("youtube_link") || "YouTube Link"} {index + 1}
                        </label>
                        <input
                          id={`youtube-link-${songId}-${index}`}
                          type="url"
                          value={link}
                          onChange={(e) => updateLinkAt(index, e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5932EA]"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLinkAt(index)}
                        className="mt-6 p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                        aria-label={t("removeYouTubeVideo") || "Remove video"}
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    </div>

                    {previewEmbed && (
                      <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700">
                        <iframe
                          width="100%"
                          height="100%"
                          className="absolute inset-0 w-full h-full"
                          src={previewEmbed}
                          title={`YouTube preview ${index + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {youtubeLinks.length < MAX_YOUTUBE_VIDEOS && (
              <button
                type="button"
                onClick={addLinkSlot}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/20 transition-colors"
              >
                <HiOutlinePlus className="w-4 h-4" />
                {t("addAnotherYouTubeVideo") || "Add another video"}
              </button>
            )}

            {youtubeError && <p className="text-sm text-red-600">{youtubeError}</p>}

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={youtubeSaving}
                className="px-4 py-2 bg-[#5932EA] text-white text-sm font-medium rounded-lg hover:bg-[#4a28c7] transition-colors disabled:opacity-60"
              >
                {youtubeSaving ? (t("loading") || "Saving...") : (t("save") || "Save")}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={youtubeSaving}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-60"
              >
                {t("cancel") || "Cancel"}
              </button>
            </div>
          </div>
        ) : displayLinks.length > 0 ? (
          <YoutubeVideosDisplay links={displayLinks} showTransposeHint={showTransposeHint} />
        ) : (
          <div className="text-center py-10">
            <HiOutlineVideoCamera className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {t("noYouTubeVideo") || "No YouTube video linked yet."}
            </p>
            <button
              type="button"
              onClick={startEditing}
              className="px-4 py-2 bg-[#5932EA] text-white text-sm font-medium rounded-lg hover:bg-[#4a28c7] transition-colors"
            >
              {t("addYouTubeVideo") || "Add video"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
